/**
 * Support chat, practice side: maps the API objects (/api/support/practice/...)
 * onto the model the support window renders, and API errors onto its texts.
 * Pure functions only (no React, no I/O), so all of it is unit-testable.
 *
 * Model (what SupportWindow reads):
 *   conversation = { id, title, lastActivity, closed, unread, openedByTeam, last, teamRead, reference, messages? }
 *   last         = { side, kind, from: { name }, text, at } | null  (list preview, see previewLine)
 *   message      = { id, side, kind, from: { name }, text, at, editedAt, attachments }
 *                  side 'practice' | 'team', kind 'text'; or an event: side 'system', kind 'ticket'
 *                  editedAt: when the team changed the text afterwards ("Bearbeitet"), null if never
 *   attachment   = { id, width, height, contentType }             (width/height null if unknown)
 *   teamRead     = { at, readAt, from: { name } } | null            (newest team read marker)
 *   reference    = { kind: 'task', id, at, title, callerName, phone, sessionId } | null
 *
 * A ticket event ("<name> hat Ihr Anliegen aufgenommen.") is a message row the
 * team side writes, so unread counts, read ticks and ordering treat it like any
 * other message; only its look differs (a note, not a bubble).
 *
 * Every timestamp is normalized with toISOString(). The window compares ISO
 * strings lexically (read ticks, list order), which only holds when all of them
 * share one format; the API sends offsets like +02:00.
 */

export const MAX_MESSAGE_LENGTH = 4000;

export const SUPPORT_TEXT = {
  notActive: 'Der Support-Chat ist für diese Praxis nicht aktiv.',
  tooLong: `Die Nachricht ist zu lang (max. ${MAX_MESSAGE_LENGTH} Zeichen).`,
  notSent: 'Nicht gesendet. Bitte noch einmal versuchen.',
  unreachable: 'Der Support-Dienst ist gerade nicht erreichbar.',
  gone: 'Diese Anfrage gibt es nicht mehr. Schreiben Sie uns gern neu.',
  noAccess: 'Auf diese Anfrage gibt es hier keinen Zugriff.',
  taskNotFound: 'Die Aufgabe wurde nicht gefunden.',
  attachmentInvalid: 'Ein Bild ist nicht mehr verfügbar. Bitte neu anhängen.',
};

export const UPLOAD_TEXT = {
  tooLarge: 'Das Bild ist zu groß (höchstens 6 MB).',
  unsupported: 'Anhängen lassen sich nur Bilder (PNG, JPEG, WebP, GIF).',
  unreadable: 'Das Bild ließ sich nicht lesen.',
  tooMany: 'Zu viele Bilder auf einmal. Bitte zuerst senden.',
  failed: 'Das Bild konnte nicht hochgeladen werden.',
};

export const DICTATION_TEXT = {
  noSpeech: 'Es war nichts zu hören. Ist das richtige Mikrofon ausgewählt und nicht stummgeschaltet?',
  tooLong: 'Die Aufnahme ist zu lang.',
  wait: 'Bitte einen Moment warten und noch einmal versuchen.',
  unavailable: 'Das Diktat ist gerade nicht verfügbar.',
};

/** Title of a request without any text yet (it began with images only). */
export const UNTITLED = 'Anfrage';

export const toIso = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * A placeholder is not a value. The Tasks table shows "-" for an anonymous
 * caller so the column does not look empty; passed on, the task card would read
 * "- · -" and leave the team guessing whether something got lost. Placeholders
 * count as empty, and the card simply leaves that line out.
 */
export const realValue = (value) => {
  const text = String(value === null || value === undefined ? '' : value).trim();
  if (!text) return null;
  if (/^[\s\-\u2013\u2014._\u00b7/?]+$/.test(text)) return null; // only dashes, dots, question marks
  if (/^(k\.?\s?a\.?|n\/?a|unbekannt|anonym|keine angabe|nicht angegeben)$/i.test(text)) return null;
  return text;
};

/** "Vorgang vom 24.09., 14:32" in browser-local time, like the Tasks page; null without a valid time. */
export const taskTitle = (createdAt) => {
  const iso = toIso(createdAt);
  if (!iso) return null;
  const d = new Date(iso);
  const day = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `Vorgang vom ${day}, ${time}`;
};

const taskReference = ({ id, createdAt, callerName, phone, sessionId }) => {
  if (!id) return null;
  const at = toIso(createdAt);
  return {
    kind: 'task',
    id: String(id),
    at,
    title: taskTitle(at),
    callerName: realValue(callerName),
    phone: realValue(phone),
    sessionId: sessionId || null, // the call: the card links to the Tasks page filtered to it
  };
};

/** The task card of a stored conversation (API `task`). */
export const referenceFromTask = (task) => (task && task.task_id
  ? taskReference({
    id: task.task_id, createdAt: task.created_at, callerName: task.caller_name, phone: task.phone, sessionId: task.session_id,
  })
  : null);

/** The task card of a new request, from the 'deskbot-support-open' event detail. */
export const referenceFromOpenEvent = (detail) => (detail && detail.taskId
  ? taskReference({ id: detail.taskId, createdAt: detail.taskCreatedAt, callerName: detail.callerName, phone: detail.phone })
  : null);

/** The team took the request on: the note in the thread, and the list preview when it is the newest. */
const ticketNote = (name, at) => ({
  side: 'system', kind: 'ticket', from: { name }, text: `${name || 'Das DeskBot-Team'} hat Ihr Anliegen aufgenommen.`, at,
});

/** List preview of a message without text: "Bild", or "3 Bilder". */
export const imagesText = (count) => (count > 1 ? `${count} Bilder` : 'Bild');

const positiveInt = (value) => (Number.isInteger(value) && value > 0 ? value : null);

export const adaptAttachment = (a) => (a && a.id
  ? {
    id: String(a.id),
    width: positiveInt(a.width),
    height: positiveInt(a.height),
    contentType: typeof a.content_type === 'string' ? a.content_type : '',
  }
  : null);

const adaptAttachments = (list) => (Array.isArray(list) ? list.map(adaptAttachment).filter(Boolean) : []);

export const adaptMessage = (m) => {
  const name = m.sender_name || '';
  const at = toIso(m.created_at);
  if (m.kind === 'ticket') return { id: m.id, ...ticketNote(name, at), attachments: [] };
  return {
    id: m.id,
    side: m.side,
    kind: 'text',
    from: { name },
    // Practice side: written by this account (false: a colleague, shown with the name).
    // A server without the field never names anyone, as before.
    mine: m.mine !== false,
    text: typeof m.body === 'string' ? m.body : '',
    at,
    editedAt: toIso(m.edited_at),
    attachments: adaptAttachments(m.attachments),
  };
};

/** The list preview: the server's first line, "Bild" for images only, the note for a ticket event. */
const adaptLast = (l) => {
  if (!l) return null;
  const name = l.sender_name || '';
  const at = toIso(l.created_at);
  if (l.kind === 'ticket') return ticketNote(name, at);
  const preview = typeof l.preview === 'string' ? l.preview : '';
  const images = Math.max(0, Number(l.attachment_count) || 0);
  return {
    side: l.side, kind: 'text', from: { name }, mine: l.mine !== false, text: preview || (images ? imagesText(images) : ''), at,
  };
};

/** The second line of a list row: "Sie: …" for your own message, else "<name>: …" (the team or a
 * colleague); an event stands as it is. */
export const previewLine = (last) => {
  if (!last) return '';
  if (last.side === 'system') return last.text;
  if (last.side === 'practice' && last.mine) return `Sie: ${last.text}`;
  return `${last.from?.name || (last.side === 'practice' ? 'Praxis' : 'DeskBot')}: ${last.text}`;
};

/**
 * Two times per marker: `at` is how far the team has read (the time of the last
 * message read), which the ticks compare with; `readAt` is when that happened,
 * which the tooltip shows (null if the server does not send it).
 */
const adaptTeamRead = (r) => {
  const at = r ? toIso(r.at) : null;
  return at ? { at, readAt: toIso(r.read_at), from: { name: r.name || '' } } : null;
};

/** One conversation: a list item, or the detail (then with `messages`). */
export const adaptConversation = (c) => {
  if (!c || !c.conversation_id) return null;
  const last = adaptLast(c.last_message);
  const conversation = {
    id: c.conversation_id,
    // The server's title is the first text; a request that began with images has none yet.
    title: typeof c.title === 'string' && c.title.trim() ? c.title : UNTITLED,
    lastActivity: (last && last.at) || toIso(c.created_at),
    closed: !!c.closed,
    unread: Math.max(0, Number(c.unread_count) || 0),
    // The team wrote first ("Praxis anschreiben"): the start view then says "hat Ihnen geschrieben".
    openedByTeam: c.opened_by_side === 'team',
    last,
    teamRead: adaptTeamRead(c.team_read),
    reference: referenceFromTask(c.task),
  };
  if (Array.isArray(c.messages)) conversation.messages = c.messages.map(adaptMessage);
  return conversation;
};

const newestFirst = (a, b) => {
  const x = a || '';
  const y = b || '';
  if (x === y) return 0;
  return x < y ? 1 : -1;
};

/** Open requests first, then by latest activity (newest on top). */
export const sortConversations = (list) => [...list].sort((a, b) => (
  (a.closed === b.closed ? 0 : a.closed ? 1 : -1)
  || newestFirst(a.lastActivity, b.lastActivity)
  || newestFirst(a.id, b.id)
));

export const adaptConversations = (rows) => sortConversations(
  (Array.isArray(rows) ? rows : []).map(adaptConversation).filter(Boolean),
);

/** His recording cap, when the server does not say (it allows at most 120 s). */
const MAX_DICTATION_SECONDS = 120;

/**
 * What the server offers. Fail closed: a flag or limit an older backend does not
 * send means that control is not shown. Images need the flag AND both limits
 * (count per message, bytes per image); dictation additionally needs a browser
 * that can record, which the window checks.
 */
export const adaptFeatures = (features) => {
  const f = features && typeof features === 'object' ? features : {};
  const maxImages = positiveInt(f.max_images);
  const maxImageBytes = positiveInt(f.max_image_bytes);
  return {
    readReceipts: f.read_receipts === true,
    images: f.images === true && !!maxImages && !!maxImageBytes,
    maxImages: maxImages || 0,
    maxImageBytes: maxImageBytes || 0,
    dictation: f.dictation === true,
    maxDictationSeconds: positiveInt(f.max_dictation_seconds) || MAX_DICTATION_SECONDS,
  };
};

// ─── Errors ─────────────────────────────────────────────────────────────────

export const errorStatus = (err) => (err && err.response ? err.response.status : null);

export const errorCode = (err) => {
  const detail = err && err.response && err.response.data ? err.response.data.detail : null;
  return detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.code || null : null;
};

const NOT_ACTIVE_CODES = new Set(['support_not_linked', 'support_test_mode', 'support_forbidden']);

/** The chat is not (or no longer) available here: not linked, or not for this user in this mode. */
export const isNotActiveError = (err) => NOT_ACTIVE_CODES.has(errorCode(err));

/** The request itself is gone (deleted, or not in this practice / scope). */
export const isGoneError = (err) => errorStatus(err) === 404 && !isNotActiveError(err);

export const sendErrorText = (err) => {
  if (isNotActiveError(err)) return SUPPORT_TEXT.notActive;
  const code = errorCode(err);
  if (code === 'task_not_found') return SUPPORT_TEXT.taskNotFound;
  // An image of the draft is no pending upload of this user any more (sent, swept).
  if (code === 'attachment_invalid') return SUPPORT_TEXT.attachmentInvalid;
  if (errorStatus(err) === 422) return SUPPORT_TEXT.tooLong;
  return SUPPORT_TEXT.notSent;
};

/** "Not active" first, then the text for the server's code (a Map: no code hits a prototype), else `fallback`. */
const textByCode = (texts, fallback) => (err) => (
  isNotActiveError(err) ? SUPPORT_TEXT.notActive : texts.get(errorCode(err)) || fallback
);

/** Why an image upload failed (anything else, a timeout included: "konnte nicht hochgeladen werden"). */
export const uploadErrorText = textByCode(new Map([
  ['image_too_large', UPLOAD_TEXT.tooLarge],
  ['unsupported_image', UPLOAD_TEXT.unsupported],
  ['image_unreadable', UPLOAD_TEXT.unreadable],
  ['too_many_uploads', UPLOAD_TEXT.tooMany],
]), UPLOAD_TEXT.failed);

export const dictationErrorText = textByCode(new Map([
  ['no_speech', DICTATION_TEXT.noSpeech],
  ['audio_too_large', DICTATION_TEXT.tooLong],
  ['dictation_busy', DICTATION_TEXT.wait],
  ['dictation_limit', DICTATION_TEXT.wait],
]), DICTATION_TEXT.unavailable);

export const loadErrorText = (err) => {
  if (isNotActiveError(err)) return SUPPORT_TEXT.notActive;
  if (errorStatus(err) === 403) return SUPPORT_TEXT.noAccess;
  return SUPPORT_TEXT.unreachable;
};

// ─── Local merges into the raw API caches (reconciled by the next refetch) ──

/** `last_message` for a message this client just sent. */
export const lastMessageOf = (m) => ({
  id: m.id,
  side: m.side,
  kind: m.kind || 'text',
  sender_name: m.sender_name,
  created_at: m.created_at,
  preview: m.body,
  attachment_count: Array.isArray(m.attachments) ? m.attachments.length : 0,
});

/** A conversation the server just returned: merged into its list row, or added on top. */
export const upsertConversation = (rows, conv) => {
  if (!conv || !conv.conversation_id) return rows;
  const list = Array.isArray(rows) ? rows : [];
  const i = list.findIndex((c) => c.conversation_id === conv.conversation_id);
  if (i === -1) return [conv, ...list];
  const next = list.slice();
  next[i] = { ...list[i], ...conv };
  return next;
};

/** Changes fields of one list row that is already there. */
export const patchConversation = (rows, conversationId, patch) => (Array.isArray(rows)
  ? rows.map((c) => (c.conversation_id === conversationId ? { ...c, ...patch } : c))
  : rows);

/** A follow-up the server accepted: appended once, and the request is open again. */
export const appendMessage = (detail, message) => {
  if (!detail || !message || !message.id) return detail;
  const messages = Array.isArray(detail.messages) ? detail.messages : [];
  return {
    ...detail,
    closed: false,
    last_message: lastMessageOf(message),
    messages: messages.some((m) => m.id === message.id) ? messages : [...messages, message],
  };
};
